import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import './ChecklistList.css';
import { FaTruck, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import ChecklistModal from './ChecklistModal';
import ManutencaoModal from './ManutencaoModal';

interface ChecklistPendencia {
  id: number;
  placa: string;
  cor_sider: string;
  banco_rasgado: number;
  todas_travessas: number;
  faltam_travessas: number;
  todas_travessas_correntes: number;
  todas_colunas_correntes: number;
  colunas_sem_corrente: string;
  sistema_pneumatico_vazamento: number;
  sistema_pneumatico_levanta4: number;
  banheira_pintada: number;
  freio_ok: number;
  todas_fivelas_instaladas: number;
  faltam_fivelas: number;
  observacoes: string;
  data: string;
  azul_troca?: number;
  borracharia?: string;
  medidas_lado_esquerdo?: string;
  medidas_lado_direito?: string;
  pneus_condicao?: string;
}

const ChecklistList: React.FC = () => {
  const [pendencias, setPendencias] = useState<ChecklistPendencia[]>([]);
  const [todos, setTodos] = useState<ChecklistPendencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showManutencao, setShowManutencao] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistPendencia | null>(null);
  const [editingChecklistId, setEditingChecklistId] = useState<number | null>(null);

  const fetchData = () => {
    setLoading(true);
    fetch('/api/checklist')
      .then(res => res.json())
      .then(data => {
        setTodos(data);
        setLoading(false);
      });
    fetch('/api/checklist/pendencias')
      .then(res => res.json())
      .then(data => setPendencias(data));
  };

  useEffect(() => {
    fetchData();
    // Atualizar ao receber evento de novo checklist
    const handler = () => fetchData();
    window.addEventListener('checklist-updated', handler);
    return () => window.removeEventListener('checklist-updated', handler);
  }, []);

  // Resumo
  const total = todos.length;
  const totalAzul = todos.filter(c => c.cor_sider === 'azul').length;
  const totalCinza = todos.filter(c => c.cor_sider === 'cinza').length;
  const totalProblemas = pendencias.length;
  const totalEmDia = total - totalProblemas;

  // Função helper para carregar imagem de forma assíncrona
  function carregarImagem(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // Função para gerar PDF com informações da carreta
  async function gerarPdfCarreta(item: ChecklistPendencia) {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Checklist Carreta - Placa: ${item.placa}`, 10, 20);
    doc.setFontSize(12);
    doc.text(`Data: ${new Date(item.data).toLocaleString()}`, 10, 30);
    doc.text(`Cor do Sider: ${item.cor_sider}`, 10, 40);
    let y = 50;
    
    // Medidas
    const medidasEsq = item.medidas_lado_esquerdo ? JSON.parse(item.medidas_lado_esquerdo) : [];
    const medidasDir = item.medidas_lado_direito ? JSON.parse(item.medidas_lado_direito) : [];
    
    if (medidasEsq.length > 0 || medidasDir.length > 0) {
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Medidas da Carreta:', 10, y);
      doc.setFont('helvetica', 'normal');
      y += 8;
      
      // Dimensões originais da imagem no formulário: 900px largura x altura proporcional
      const imgOriginalWidth = 900;
      const containerOriginalHeight = 300; // Altura do container div onde clicamos
      // Imagem no PDF - menor para caber as duas na mesma página
      const imgWidth = 180;
      const imgHeight = 60; // Mantendo a mesma proporção visual
      
      // Adicionar imagem do lado esquerdo com medidas
      if (medidasEsq.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Lado Esquerdo da Carreta', 10, y);
        doc.setFont('helvetica', 'normal');
        y += 5;
        
        // Carregar imagem de forma assíncrona
        const imgEsquerdo = await carregarImagem('/images/checklist/sider-esquerdo.png');
        
        const imgYStart = y;
        doc.addImage(imgEsquerdo, 'PNG', 10, y, imgWidth, imgHeight);
        
        // Adicionar as medidas sobre a imagem
        doc.setFontSize(7);
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'bold');
        
        medidasEsq.forEach((m: any) => {
          // As coordenadas m.x e m.y são do canto superior esquerdo da caixa
          // Calcular o centro da caixa para posicionamento preciso
          const caixaWidth = m.width || 120;
          const caixaHeight = m.height || 30;
          const centroX = m.x + (caixaWidth / 2);
          const centroY = m.y + (caixaHeight / 2);
          
          // Usar as dimensões reais do container quando a medida foi adicionada
          // Se não existir (dados antigos), usar os valores padrão
          const containerWidth = m.containerWidth || imgOriginalWidth;
          const containerHeight = m.containerHeight || containerOriginalHeight;
          
          // Escalar proporcionalmente para o PDF
          const xPdf = 10 + ((centroX / containerWidth) * imgWidth);
          const yPdf = imgYStart + ((centroY / containerHeight) * imgHeight);
          
          // Fundo branco para o texto
          const textoCompleto = `${m.label}: ${m.value}`;
          const textoWidth = doc.getTextWidth(textoCompleto);
          
          // Centralizar o texto na posição calculada
          const xTexto = xPdf - (textoWidth / 2);
          
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(255, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(xTexto - 0.5, yPdf - 2.5, textoWidth + 1, 3.5, 'FD');
          
          // Texto centralizado
          doc.text(textoCompleto, xTexto, yPdf);
        });
        
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setDrawColor(0, 0, 0);
        y += imgHeight + 8;
      }
      
      // Adicionar imagem do lado direito com medidas
      if (medidasDir.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Lado Direito da Carreta', 10, y);
        doc.setFont('helvetica', 'normal');
        y += 5;
        
        // Carregar imagem de forma assíncrona
        const imgDireito = await carregarImagem('/images/checklist/sider-direito.png');
        
        const imgYStart = y;
        doc.addImage(imgDireito, 'PNG', 10, y, imgWidth, imgHeight);
        
        // Adicionar as medidas sobre a imagem
        doc.setFontSize(7);
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'bold');
        
        medidasDir.forEach((m: any) => {
          // As coordenadas m.x e m.y são do canto superior esquerdo da caixa
          // A caixa tem width e height, vamos usar o centro dela
          const caixaWidth = m.width || 120;
          const caixaHeight = m.height || 30;
          
          // Centro da caixa na tela original
          const centroX = m.x + (caixaWidth / 2);
          const centroY = m.y + (caixaHeight / 2);
          
          // Usar as dimensões reais do container quando a medida foi adicionada
          // Se não existir (dados antigos), usar os valores padrão
          const containerWidth = m.containerWidth || imgOriginalWidth;
          const containerHeight = m.containerHeight || containerOriginalHeight;
          
          // Escalar para o PDF
          const xPdf = 10 + ((centroX / containerWidth) * imgWidth);
          const yPdf = imgYStart + ((centroY / containerHeight) * imgHeight);
          
          // Fundo branco para o texto
          const textoCompleto = `${m.label}: ${m.value}`;
          const textoWidth = doc.getTextWidth(textoCompleto);
          
          // Centralizar o texto na posição calculada
          const xTexto = xPdf - (textoWidth / 2);
          
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(255, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(xTexto - 0.5, yPdf - 2.5, textoWidth + 1, 3.5, 'FD');
          
          // Texto centralizado
          doc.text(textoCompleto, xTexto, yPdf);
        });
        
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setDrawColor(0, 0, 0);
        y += imgHeight + 8;
      }
      
      y += 3;
    }
    
    doc.setFontSize(12);
    const pendencias: string[] = [];
    if (item.banco_rasgado) pendencias.push('Banco rasgado');
    if (item.sistema_pneumatico_vazamento) pendencias.push('Vazamento pneumático');
    if (item.todas_travessas === 0) pendencias.push(`Faltam travessas (${item.faltam_travessas || '?'})`);
    if (item.todas_travessas_correntes === 0) pendencias.push('Travessas sem correntes');
    if (item.todas_colunas_correntes === 0) pendencias.push(`Colunas sem correntes ${item.colunas_sem_corrente ? '(' + item.colunas_sem_corrente + ')' : ''}`);
    if (item.sistema_pneumatico_levanta4 === 0) pendencias.push('Não levanta os 4 lados');
    if (item.banheira_pintada === 0) pendencias.push('Banheira não pintada');
    if (item.freio_ok === 0) pendencias.push('Freio não está OK');
    if (item.todas_fivelas_instaladas === 0) pendencias.push(`Faltam fivelas (${item.faltam_fivelas || '?'})`);
    if (item.observacoes) pendencias.push(`Obs: ${item.observacoes}`);
    doc.text('Pendências:', 10, y);
    y += 10;
    if (pendencias.length === 0) {
      doc.text('Nenhuma pendência registrada.', 15, y);
    } else {
      pendencias.forEach(p => {
        doc.text(`- ${p}`, 15, y);
        y += 8;
      });
    }
    doc.save(`Checklist_Carreta_${item.placa}.pdf`);
  }

  return (
    <div className="checklist-dashboard checklist-dashboard-modern">
      <div className="checklist-header-bar">
        <span className="checklist-title"><span style={{marginRight: 8, display: 'inline-flex'}}><FaTruck size={32} /></span> Checklist Carretas</span>
        <button className="checklist-btn checklist-btn-primary" onClick={() => {
          setEditingChecklistId(null);
          setShowForm(true);
        }}>
          + Novo Checklist
        </button>
        <ChecklistModal 
          open={showForm} 
          onClose={() => {
            setShowForm(false);
            setEditingChecklistId(null);
          }}
          checklistId={editingChecklistId}
        />
      </div>
      <div className="checklist-cards-row checklist-cards-row-modern">
        <div className="checklist-card checklist-card-total checklist-card-modern">
          <FaTruck size={36} />
          <div className="checklist-card-value">{total}</div>
          <div className="checklist-card-label">Total de Carretas</div>
        </div>
        <div className="checklist-card checklist-card-azul checklist-card-modern">
          <span style={{fontSize: 36}}>🔵</span>
          <div className="checklist-card-value">{totalAzul}</div>
          <div className="checklist-card-label">Total Azul</div>
        </div>
        <div className="checklist-card checklist-card-cinza checklist-card-modern">
          <span style={{fontSize: 36}}>⚪</span>
          <div className="checklist-card-value">{totalCinza}</div>
          <div className="checklist-card-label">Total Cinza</div>
        </div>
        <div className="checklist-card checklist-card-ok checklist-card-modern">
          <FaCheckCircle size={36} color="#27ae60" />
          <div className="checklist-card-value">{totalEmDia}</div>
          <div className="checklist-card-label">Em Dia</div>
        </div>
        <div className="checklist-card checklist-card-problema checklist-card-modern">
          <FaExclamationTriangle size={36} color="#e67e22" />
          <div className="checklist-card-value">{totalProblemas}</div>
          <div className="checklist-card-label">Com Problemas</div>
        </div>
      </div>
      <div className="checklist-table-area">
        <div className="checklist-table-title">Pendências das Carretas</div>
        {loading ? (
          <div>Carregando...</div>
        ) : pendencias.length === 0 ? (
          <div className="checklist-table-ok">Todas as carretas estão em dia!</div>
        ) : (
          <table className="checklist-table checklist-table-modern">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Sider</th>
                <th>Medidas</th>
                <th>Fivelas</th>
                <th>Troca</th>
                <th>Bando</th>
                <th>Travessas</th>
                <th>Colunas</th>
                <th>Banheira</th>
                <th>Freios</th>
                <th>Borracharia</th>
                <th>Observação</th>
                <th>Manutenção</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pendencias.map(item => {
                let medidasEsq = [];
                let medidasDir = [];
                
                try {
                  medidasEsq = item.medidas_lado_esquerdo ? JSON.parse(item.medidas_lado_esquerdo) : [];
                } catch (e) {
                  // Dados corrompidos - ignora silenciosamente
                  medidasEsq = [];
                }
                
                try {
                  medidasDir = item.medidas_lado_direito ? JSON.parse(item.medidas_lado_direito) : [];
                } catch (e) {
                  // Dados corrompidos - ignora silenciosamente
                  medidasDir = [];
                }
                
                const totalMedidas = medidasEsq.length + medidasDir.length;
                
                return (
                <tr key={item.id}>
                  <td>
                    <button
                      className="checklist-placa-btn"
                      onClick={() => gerarPdfCarreta(item)}
                      title="Gerar PDF com informações da carreta"
                      style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                    >
                      {item.placa}
                    </button>
                  </td>                  
                  <td>{item.cor_sider === 'azul' ? 'Azul' : item.cor_sider === 'cinza' ? 'Cinza' : item.cor_sider}</td>
                  <td style={{ fontSize: 12 }}>
                    {totalMedidas > 0 ? (
                      <div title="Clique na placa para ver detalhes no PDF" style={{ cursor: 'help' }}>
                        <div style={{ color: '#3498db', fontWeight: 600 }}>📏 {totalMedidas} medida{totalMedidas > 1 ? 's' : ''}</div>
                        <div style={{ fontSize: 10, color: '#7f8c8d', marginTop: 2 }}>
                          {medidasEsq.length > 0 && <span>Esq: {medidasEsq.length}</span>}
                          {medidasEsq.length > 0 && medidasDir.length > 0 && <span> | </span>}
                          {medidasDir.length > 0 && <span>Dir: {medidasDir.length}</span>}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#95a5a6' }}>-</span>
                    )}
                  </td>
                  <td style={{color: item.faltam_fivelas > 0 ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.faltam_fivelas || '0'}</td>
                  <td>{item.azul_troca === 1 ? 'Sim' : 'Não'}</td>
                  <td style={{color: item.banco_rasgado ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.banco_rasgado ? 'Sim' : 'Não'}</td>
                  <td style={{color: !item.todas_travessas ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.todas_travessas ? 'OK' : (item.faltam_travessas || '0')}</td>
                  <td style={{color: !item.todas_colunas_correntes ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.todas_colunas_correntes ? 'OK' : (item.colunas_sem_corrente || '0')}</td>
                  <td style={{color: !item.banheira_pintada ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.banheira_pintada ? 'Pintada' : 'Não'}</td>
                  <td style={{color: !item.freio_ok ? '#c0392b' : '#27ae60', fontWeight: 'bold'}}>{item.freio_ok ? 'Sim' : 'Não'}</td>
                  <td style={{
                    color: item.pneus_condicao === 'bons' ? '#27ae60' : item.pneus_condicao ? '#c0392b' : '#95a5a6',
                    fontWeight: 'bold'
                  }}>
                    {item.pneus_condicao === 'bons' ? 'Bons' : 
                     item.pneus_condicao === 'marca_fogo' ? 'Marca de fogo' :
                     item.pneus_condicao === 'com_avaria' ? 'Com avaria' : '-'}
                  </td>
                  <td>{item.observacoes || '-'}</td>
                  <td>
                    <button
                      className="checklist-btn-manutencao"
                      onClick={() => {
                        setSelectedChecklist(item);
                        setShowManutencao(true);
                      }}
                      title="Registrar manutenção realizada"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      🔧 Registrar
                    </button>
                  </td>
                  <td>
                    <button
                      className="checklist-btn-editar"
                      onClick={() => {
                        setEditingChecklistId(item.id);
                        setShowForm(true);
                      }}
                      title="Editar checklist"
                      style={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 87, 108, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      ✏️ Editar
                    </button>
                  </td>                  
                </tr>
              )})}
          </tbody>
          </table>
        )}
      </div>
      {selectedChecklist && (
        <ManutencaoModal
          open={showManutencao}
          onClose={() => {
            setShowManutencao(false);
            setSelectedChecklist(null);
          }}
          checklist={selectedChecklist}
        />
      )}
    </div>
  );
};
export default ChecklistList;